import { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Play, Users, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Game } from '../../types/game';
import { LINE_GRADIENT } from '../../constants/gradients';

interface GameCarouselProps {
  games: Game[];
}

export default function GameCarousel({ games }: GameCarouselProps) {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isContentVisible, setIsContentVisible] = useState(true);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);

  const transitionToIndex = (newIndex: number) => {
    setIsContentVisible(false);
    setTimeout(() => {
      setCurrentIndex(newIndex);
      setIsContentVisible(true);
    }, 300);
  };

  const goToPrevious = () => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : games.length - 1;
    transitionToIndex(newIndex);
  };

  const goToNext = () => {
    const newIndex = currentIndex < games.length - 1 ? currentIndex + 1 : 0;
    transitionToIndex(newIndex);
  };

  const goToIndex = (index: number) => {
    if (index !== currentIndex) {
      transitionToIndex(index);
    }
  };

  // Auto-play functionality
  useEffect(() => {
    if (isAutoPlaying && games.length > 1) {
      autoPlayRef.current = setInterval(() => {
        goToNext();
      }, 5000); // Change slide every 5 seconds

      return () => {
        if (autoPlayRef.current) {
          clearInterval(autoPlayRef.current);
        }
      };
    }
  }, [isAutoPlaying, currentIndex, games.length]);

  // Pause auto-play on hover
  const handleMouseEnter = () => {
    setIsAutoPlaying(false);
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
  };

  const handleMouseLeave = () => {
    setIsAutoPlaying(true);
  };

  if (games.length === 0) return null;

  const currentGame = games[currentIndex];

  return (
    <div 
      className="relative w-full rounded-2xl overflow-hidden group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Background Image with Overlay */}
      <div 
        className="relative w-full h-[500px] cursor-pointer"
        onClick={() => navigate(`/games/${currentGame.id}`)}
      >
        {games.map((game, index) => (
          <div
            key={game.id}
            className={`absolute inset-0 transition-opacity duration-500 ${
              index === currentIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={game.cover_image_url || '/placeholder-game.png'}
              alt={game.name}
              className="w-full h-full object-cover"
              style={{
                transform: 'scale(1.1)',
                animation: 'panLeft 30s linear infinite'
              }}
            />
            {/* Dark gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
          </div>
        ))}

        {/* Content Overlay */}
        <div 
          className={`absolute inset-0 flex items-center z-10 pointer-events-none transition-opacity duration-300 ${
            isContentVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="container mx-auto px-20 md:px-24">
            <div className="max-w-2xl">
              {/* Game Title */}
              <div className="flex items-center gap-4 mb-4">
                {currentGame.icon_url && (
                  <img 
                    src={currentGame.icon_url} 
                    alt={`${currentGame.name} icon`}
                    className="w-24 h-24 md:w-28 md:h-28 rounded-xl object-cover shadow-lg"
                  />
                )}
                <h2 className="text-5xl md:text-6xl font-bold text-white">
                  {currentGame.name}
                </h2>
              </div>

              {/* Game Description */}
              <p className="text-lg text-gray-200 mb-6 line-clamp-3">
                {currentGame.description || 'Experience an amazing interactive game that brings entertainment to the next level.'}
              </p>

              {/* Stats */}
              <div className="flex items-center gap-6 mb-8">
                {currentGame.total_plays !== undefined && (
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-gray-300" />
                    <span className="text-white font-medium">
                      {currentGame.total_plays.toLocaleString()} plays
                    </span>
                  </div>
                )}
                {currentGame.total_likes !== undefined && (
                  <div className="flex items-center gap-2">
                    <Heart className="w-5 h-5 text-gray-300" />
                    <span className="text-white font-medium">
                      {currentGame.total_likes.toLocaleString()} likes
                    </span>
                  </div>
                )}
              </div>

              {/* Play Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/games/${currentGame.id}`);
                }}
                className="px-8 py-4 text-white text-lg font-semibold rounded-xl flex items-center gap-3 hover:scale-105 active:scale-95 transition-all duration-200 pointer-events-auto"
                style={{ background: LINE_GRADIENT }}
              >
                <Play className="w-6 h-6 fill-current" />
                Play Now
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      {games.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              goToPrevious();
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/10 backdrop-blur-sm border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-black/20 hover:scale-110 z-30"
            aria-label="Previous"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/10 backdrop-blur-sm border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-black/20 hover:scale-110 z-30"
            aria-label="Next"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {games.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {games.map((_, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                goToIndex(index);
              }}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'w-8'
                  : 'w-2 hover:w-4'
              }`}
              style={{
                background: index === currentIndex 
                  ? LINE_GRADIENT
                  : 'rgba(255, 255, 255, 0.4)'
              }}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
